import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Linkedin, Award } from 'lucide-react';
import tftLogo from '@/assets/tft-logo.png';
import { useLanguage } from '@/contexts/LanguageContext';
import { industries } from '@/data/industries';

const Footer = () => {
  const { language, t } = useLanguage();

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img src={tftLogo} alt="TFT" className="h-16 mb-4 brightness-0 invert" width="94" height="64" />
            <p className="text-sm opacity-90 mb-4">
              {t('Πρωταγωνιστές στις εφαρμογές Τεχνολογίας για τη Βιομηχανία Τροφίμων και Ποτών, από το 1963', 'Leaders in Technology applications for the Food and Beverage Industry, since 1963')}
            </p>
            <div className="flex space-x-3 mt-4">
              <a 
                href="https://www.facebook.com/TESCOFOODTECHNOLOGY/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary-light transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="h-6 w-6" />
              </a>
              <a 
                href="https://www.linkedin.com/company/tesco-engineering-s-a-/posts/?feedView=all" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-primary-light transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{t('Γρήγοροι Σύνδεσμοι', 'Quick Links')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/company" className="hover:text-primary-light transition-colors">{t('Εταιρεία', 'Company')}</Link></li>
              <li><Link to="/industries" className="hover:text-primary-light transition-colors">{t('Κλάδοι', 'Industries')}</Link></li>
              <li><Link to="/partners" className="hover:text-primary-light transition-colors">{t('Συνεργάτες', 'Partners')}</Link></li>
              <li><Link to="/support" className="hover:text-primary-light transition-colors">{t('Υποστήριξη', 'Support')}</Link></li>
              <li><Link to="/contact" className="hover:text-primary-light transition-colors">{t('Επικοινωνία', 'Contact')}</Link></li>
            </ul>

            {/* Certifications highlight */}
            <Link
              to="/certifications"
              className="mt-6 inline-flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-4 py-2.5 text-sm font-semibold backdrop-blur-sm transition-all hover:bg-white hover:text-primary hover:border-white"
            >
              <Award className="h-5 w-5" />
              {t('Πιστοποιήσεις', 'Certifications')}
            </Link>
          </div>

          {/* Industries */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{t('Κλάδοι', 'Industries')}</h4>
            <ul className="space-y-2 text-sm">
              {industries.map((industry) => (
                <li key={industry.slug}>
                  <Link to={`/industries/${industry.slug}`} className="hover:text-primary-light transition-colors">
                    {industry.title[language]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{t('Επικοινωνία', 'Contact')}</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start">
                <MapPin className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>{t('Παραδείσου 10, 15125 Μαρούσι', 'Paradeisou 10, 15125 Marousi')}</span>
              </li>
              <li className="flex items-center">
                <Phone className="h-5 w-5 mr-2 flex-shrink-0" />
                <a href="tel:+302130070100" className="hover:text-primary-light transition-colors">
                  +30 213 007 0100
                </a>
              </li>
              <li className="flex items-center">
                <Mail className="h-5 w-5 mr-2 flex-shrink-0" />
                <a href="mailto:info@tft.gr" className="hover:text-primary-light transition-colors">
                  info@tft.gr
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm">
          <p className="opacity-90">&copy; {new Date().getFullYear()} TFT - Tesco Food Technology S.A. {t('Όλα τα δικαιώματα κατοχυρωμένα', 'All rights reserved')}.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/legal/privacy" className="hover:text-primary-light transition-colors">{t('Πολιτική Απορρήτου', 'Privacy Policy')}</Link>
            <Link to="/legal/terms" className="hover:text-primary-light transition-colors">{t('Όροι Χρήσης', 'Terms of Use')}</Link>
            <Link to="/legal/cookies" className="hover:text-primary-light transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;